import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OriginalAmountAssetType, SwapDuration } from '@libs/backend-common';
import { useDebounce } from '@libs/hooks';
import { CurrencyType, NetworksType, RateTypes, SWAP_CALCULATOR_DEBOUCED_TIME, } from '@libs/types';
import { useSwapAssetSelectOptions } from './useSwapAssetSelectOptions';
const defaultAssetValue = {
    label: '',
    value: '',
    networkId: 0,
    assetCode: '',
    roundingPrecision: 2,
};
const PREFERED_BASE_ASSET = CurrencyType.BTC;
export const useSwapCalculator = (setState, destinationAddress, qoutesAssetObj, setQouteAssetObj, setDestinationAddress, clearAddrValidationErr, onSwapCreation, setBaseAssetNetworkCode, swapOptions, uuid, initSwapError, swapCalculationData, shouldInit, initSwapResponse, swapCalculationResponse, email, refundAddress, isValidEmail, isValidRefundAddress, addrValidationErr) => {
    const navigate = useNavigate();
    const location = useLocation();
    const urlState = location.state;
    const [selectedDuration, setSelectedDuration] = useState(SwapDuration.RAPID);
    const [baseAssetAmount, setBaseAssetAmount] = useState(() => {
        if (urlState?.queryArgs?.originalAmountAssetType ===
            OriginalAmountAssetType.SOURCE) {
            return String(urlState.queryArgs.amount);
        }
        else {
            return '';
        }
    });
    const debouncedBaseAssetAmount = useDebounce(baseAssetAmount, SWAP_CALCULATOR_DEBOUCED_TIME);
    const [qoutesAssetAmount, setQouteAssetAmount] = useState(() => {
        if (urlState?.queryArgs?.originalAmountAssetType ===
            OriginalAmountAssetType.DESTINATION) {
            return String(urlState.queryArgs.amount);
        }
        else {
            return '';
        }
    });
    const debouncedQouteAssetAmount = useDebounce(qoutesAssetAmount, SWAP_CALCULATOR_DEBOUCED_TIME);
    const [baseAsset, setBaseAsset] = useState(() => {
        if (urlState?.queryArgs?.sourceAssetCode &&
            !urlState?.preferedBaseAsset) {
            const { sourceNetworkId, sourceAssetCode } = urlState.queryArgs;
            const value = sourceAssetCode + '-' + sourceNetworkId;
            return {
                roundingPrecision: urlState.baseAssetRoundingPrecision || 2,
                value,
                label: sourceAssetCode,
                networkId: urlState.queryArgs.sourceNetworkId || 0,
                assetCode: sourceAssetCode,
            };
        }
        return defaultAssetValue;
    });
    useEffect(() => {
        if (urlState?.sourceNetworkCode && setBaseAssetNetworkCode) {
            setBaseAssetNetworkCode(urlState.sourceNetworkCode);
        }
    }, [setBaseAssetNetworkCode, urlState?.sourceNetworkCode]);
    useEffect(() => {
        if (urlState?.queryArgs?.destinationAssetCode &&
            !urlState?.preferedQouteAsset) {
            const { destinationNetworkId, destinationAssetCode } = urlState.queryArgs;
            const value = destinationAssetCode + '-' + destinationNetworkId;
            setQouteAssetObj({
                value,
                label: destinationAssetCode,
                networkId: urlState.queryArgs.destinationNetworkId || 0,
                assetCode: destinationAssetCode,
                roundingPrecision: urlState.quoteAssetRoundingPrecision || 2,
            });
        }
        else {
            setQouteAssetObj(defaultAssetValue);
        }
    }, [setQouteAssetObj, urlState]);
    const [swapTaskName, setSwapTaskName] = useState('');
    const [addressName, setAddressName] = useState('');
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [shouldSaveAddress, setShouldSaveAddress] = useState(false);
    const [saveAddressClicked, setSaveAddressClicked] = useState(false);
    const savedAddressRef = useRef();
    const handleAddressChange = (e) => {
        clearAddrValidationErr();
        const { value } = e.target;
        setDestinationAddress(value);
        const savedAddress = savedAddressRef.current;
        if (saveAddressClicked) {
            setSaveAddressClicked(false);
        }
        if (savedAddress && savedAddress !== value) {
            setShouldSaveAddress(false);
        }
    };
    const onAddressSave = (addressName) => {
        setAddressName(addressName);
        savedAddressRef.current = destinationAddress;
    };
    useDebounce(swapTaskName, 200);
    const [originalAmountAssetType, setOriginalAmountAssetType] = useState(urlState?.queryArgs?.originalAmountAssetType ||
        OriginalAmountAssetType.SOURCE);
    const [taskId, setTaskId] = useState(urlState?.queryArgs?.taskId || undefined);
    const [rateType, setRateType] = useState(urlState?.queryArgs?.rateType || RateTypes.FLOAT);
    const DEFAULT_UNSTABLE_ASSET_BASE_AMOUNT = 0.1;
    const baseAssetObject = useMemo(() => swapOptions?.find((e) => e.code === baseAsset.assetCode), [baseAsset, swapOptions]);
    const basePairedAssets = useSwapAssetSelectOptions(baseAssetObject?.pairedAssets);
    //TODO tmp before same asset network support will be available
    const qoutesAssetOptions = useMemo(() => {
        return basePairedAssets.reduce((acc, item) => {
            if (!(item.assetCode === baseAsset?.assetCode &&
                item?.networkId === baseAsset?.networkId)) {
                acc.push(item);
            }
            return acc;
        }, []);
    }, [baseAsset, basePairedAssets]);
    const baseAssetOptions = useSwapAssetSelectOptions(swapOptions);
    const isReverseTradePossible = useMemo(() => {
        const quoteAssetsInBaseOptions = swapOptions?.find((e) => e.code === qoutesAssetObj.assetCode);
        if (!quoteAssetsInBaseOptions) {
            return false;
        }
        const baseAssetInQouteOptions = quoteAssetsInBaseOptions.pairedAssets?.find((item) => item.code === baseAsset.label);
        if (baseAssetInQouteOptions) {
            return true;
        }
        return !!baseAssetInQouteOptions;
    }, [baseAsset.label, qoutesAssetObj.assetCode, swapOptions]);
    const amount = originalAmountAssetType === OriginalAmountAssetType.SOURCE
        ? +debouncedBaseAssetAmount
        : +debouncedQouteAssetAmount;
    const calculateSwapRecord = useMemo(() => ({
        amount,
        originalAmountAssetType,
        rateType,
        destinationAssetCode: qoutesAssetObj.label,
        destinationNetworkId: qoutesAssetObj.networkId,
        sourceAssetCode: baseAsset.label,
        sourceNetworkId: baseAsset.networkId,
        ...(taskId ? { taskId } : {}),
    }), [
        amount,
        baseAsset.label,
        baseAsset.networkId,
        originalAmountAssetType,
        qoutesAssetObj.label,
        qoutesAssetObj.networkId,
        rateType,
        taskId,
    ]);
    const initSwapRecord = {
        ...calculateSwapRecord,
        email,
        refundAddress,
        destinationAddress,
        name: swapTaskName,
        duration: selectedDuration,
        destinationAddressName: addressName || undefined,
        destinationAddressId: Number(selectedAddressId) || undefined,
    };
    useEffect(() => {
        if (initSwapResponse && !initSwapError) {
            onSwapCreation(uuid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initSwapError, initSwapResponse, navigate]);
    useEffect(() => {
        setState({
            queryArgs: calculateSwapRecord,
            baseAssetRoundingPrecision: baseAsset?.roundingPrecision,
            quoteAssetRoundingPrecision: qoutesAssetObj?.roundingPrecision,
            sourceNetworkCode: baseAsset.networkCode,
        });
    }, [baseAsset, calculateSwapRecord, qoutesAssetObj, setState]);
    useEffect(() => {
        if (swapOptions && !baseAsset.value) {
            if (baseAssetOptions?.length) {
                const code = urlState?.preferedBaseAsset || PREFERED_BASE_ASSET;
                const preferredBaseAsset = baseAssetOptions.find((e) => e.label === code);
                if (preferredBaseAsset) {
                    setBaseAssetAmount(String(DEFAULT_UNSTABLE_ASSET_BASE_AMOUNT));
                    if (!(baseAsset.value === preferredBaseAsset.value)) {
                        setBaseAsset(preferredBaseAsset);
                        if (preferredBaseAsset.networkCode) {
                            setBaseAssetNetworkCode?.(preferredBaseAsset.networkCode);
                        }
                    }
                }
                else {
                    if (!(baseAsset.value === baseAssetOptions[0].value)) {
                        setBaseAsset({
                            roundingPrecision: baseAssetOptions[0].roundingPrecision,
                            label: baseAssetOptions[0].label,
                            value: baseAssetOptions[0].value,
                            networkId: baseAssetOptions[0]?.networkId,
                            assetCode: baseAssetOptions[0].assetCode,
                            networkCode: baseAssetOptions[0].networkCode,
                        });
                        if (baseAssetOptions[0].networkCode) {
                            setBaseAssetNetworkCode?.(baseAssetOptions[0].networkCode);
                        }
                    }
                }
            }
        }
    }, [
        baseAsset,
        baseAssetOptions,
        setBaseAssetNetworkCode,
        swapOptions,
        urlState?.preferedBaseAsset,
    ]);
    useEffect(() => {
        if (qoutesAssetObj) {
            const qouteExistsInNewOptions = !!qoutesAssetOptions.find((e) => e.networkId === qoutesAssetObj.networkId &&
                e.label === qoutesAssetObj.assetCode);
            if (qouteExistsInNewOptions)
                return;
        }
        if (qoutesAssetOptions?.length) {
            const findMethod = (e) => {
                if (urlState?.preferedQouteAsset) {
                    return e.value === urlState.preferedQouteAsset;
                }
                else {
                    return (e.assetCode === CurrencyType.USDT &&
                        e.fireblocksId === NetworksType.TRX_USDT_S2UZ);
                }
            };
            const preferedQouteAseet = qoutesAssetOptions.find(findMethod);
            //TODO temp solution needs fix
            if (preferedQouteAseet) {
                if (qoutesAssetObj.value !== preferedQouteAseet.value) {
                    setQouteAssetObj({
                        label: preferedQouteAseet.label,
                        value: preferedQouteAseet.value,
                        networkId: preferedQouteAseet.networkId,
                        networkCode: preferedQouteAseet.networkCode,
                        assetCode: preferedQouteAseet.assetCode,
                        roundingPrecision: preferedQouteAseet.roundingPrecision,
                    });
                }
            }
            else {
                if (qoutesAssetObj.value !== qoutesAssetOptions[0].value) {
                    setQouteAssetObj({
                        label: qoutesAssetOptions[0].label,
                        value: qoutesAssetOptions[0].value,
                        networkId: qoutesAssetOptions[0]?.networkId,
                        networkCode: qoutesAssetOptions[0]?.networkCode,
                        assetCode: qoutesAssetOptions[0].assetCode,
                        roundingPrecision: qoutesAssetOptions[0].roundingPrecision,
                    });
                }
            }
        }
    }, [qoutesAssetObj, qoutesAssetOptions, setQouteAssetObj, urlState]);
    useEffect(() => {
        if (swapCalculationData &&
            swapCalculationData.neededAmount &&
            swapCalculationData.receivedAmount) {
            if (originalAmountAssetType === OriginalAmountAssetType.SOURCE &&
                debouncedBaseAssetAmount === baseAssetAmount) {
                setQouteAssetAmount(String(swapCalculationData.receivedAmount));
            }
            else if (originalAmountAssetType === OriginalAmountAssetType.DESTINATION &&
                debouncedQouteAssetAmount === qoutesAssetAmount) {
                setBaseAssetAmount(String(swapCalculationData.neededAmount));
            }
            setTaskId(swapCalculationData.taskId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swapCalculationResponse]);
    const shouldButtonBeDisabled = !destinationAddress ||
        !isValidEmail ||
        !isValidRefundAddress ||
        !!addrValidationErr;
    return {
        baseAsset,
        setBaseAsset,
        qoutesAsset: qoutesAssetObj,
        setQouteAsset: setQouteAssetObj,
        baseAssetAmount,
        setBaseAssetAmount,
        qoutesAssetAmount,
        setQouteAssetAmount,
        baseAssetOptions,
        qoutesAssetOptions,
        setRateType,
        originalAmountAssetType,
        setOriginalAmountAssetType,
        rateType,
        shouldButtonBeDisabled: (shouldInit && shouldButtonBeDisabled) || !amount,
        address: destinationAddress,
        setAddress: setDestinationAddress,
        swapTaskName,
        setSwapTaskName,
        initSwapRecord,
        isReverseTradePossible,
        setSelectedDuration,
        selectedDuration,
        shouldSaveAddress,
        setSelectedAddressId,
        selectedAddressId,
        setShouldSaveAddress,
        onAddressSave,
        handleAddressChange,
        setSaveAddressClicked,
        setAddressName,
    };
};
//# sourceMappingURL=useSwapCalculator.js.map